/**
 * Import Permissions Modal
 *
 * Dialog for bulk importing permission → operation mappings from JSON.
 * Supports paste and file upload. Validates against known operations
 * before sending to POST /api/permissions/mappings/bulk.
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faRotate, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { bulkImportMappings } from '../../../services/permissionAdminService';
import type { FeatureOperation, BulkImportResult } from '../../../services/permissionAdminService';
import './ImportPermissionsModal.scss';
import AppModal from '../../common/AppModal';

interface ImportPermissionsModalProps {
  operations: FeatureOperation[];
  onClose: (imported: boolean) => void;
}

interface MappingEntry {
  permission: string;
  feature: string;
  operation: string;
}

const ImportPermissionsModal: React.FC<ImportPermissionsModalProps> = ({ operations, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [jsonInput, setJsonInput] = useState('');
  const [parsedMappings, setParsedMappings] = useState<MappingEntry[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Build set of valid operation keys for client-side validation
  const validOps = useMemo(() => {
    return new Set(
      operations.filter(op => op.isActive).map(op => `${op.feature}::${op.operation}`),
    );
  }, [operations]);

  const validateAndParse = useCallback((input: string) => {
    const errors: string[] = [];
    let mappings: MappingEntry[] = [];

    if (!input.trim()) {
      return { valid: false, mappings: [], errors: ['No JSON input provided'] };
    }

    try {
      const parsed = JSON.parse(input);

      // Handle both array format and { mappings: [] } export format
      if (Array.isArray(parsed)) {
        mappings = parsed;
      } else if (parsed.mappings && Array.isArray(parsed.mappings)) {
        mappings = parsed.mappings;
      } else {
        errors.push('JSON must be an array of mappings or an object with a "mappings" array');
        return { valid: false, mappings: [], errors };
      }

      if (mappings.length === 0) {
        errors.push('No mappings found in JSON');
        return { valid: false, mappings: [], errors };
      }

      // Validate each mapping
      mappings.forEach((m, index) => {
        if (!m.permission || !m.feature || !m.operation) {
          errors.push(`Entry ${index + 1}: Missing required field (permission, feature, operation)`);
          return;
        }
        const key = `${m.feature}::${m.operation}`;
        if (!validOps.has(key)) {
          errors.push(`Entry ${index + 1}: Unknown operation "${m.feature}.${m.operation}"`);
        }
      });

      return { valid: errors.length === 0, mappings, errors };
    } catch (parseError: any) {
      errors.push(`Invalid JSON: ${parseError.message}`);
      return { valid: false, mappings: [], errors };
    }
  }, [validOps]);

  const handleJsonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target.value;
    setJsonInput(input);
    setImportResult(null);
    setError(null);

    if (input.trim()) {
      const result = validateAndParse(input);
      setParsedMappings(result.mappings);
      setValidationErrors(result.errors);
    } else {
      setParsedMappings([]);
      setValidationErrors([]);
    }
  }, [validateAndParse]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonInput(content);
      setImportResult(null);
      setError(null);

      const result = validateAndParse(content);
      setParsedMappings(result.mappings);
      setValidationErrors(result.errors);
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [validateAndParse]);

  const handleImport = useCallback(async () => {
    if (parsedMappings.length === 0 || validationErrors.length > 0) return;

    setImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const result = await bulkImportMappings(parsedMappings);
      setImportResult(result);
    } catch (err: any) {
      console.error('[ImportPermissionsModal] Error importing mappings:', err);
      setError(err.message || 'Failed to import mappings');
    } finally {
      setImporting(false);
    }
  }, [parsedMappings, validationErrors]);

  const isValid = parsedMappings.length > 0 && validationErrors.length === 0;
  const didImport = importResult && importResult.created > 0;

  return (
    <AppModal
      isOpen
      onClose={() => onClose(!!didImport)}
      width={800}
      maxHeight="90vh"
      header={<h2 className="modal-title">Import Permission Mappings</h2>}
    >
      <div className="import-permissions-modal">
        {/* Error */}
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={`import-result ${importResult.errors.length > 0 ? 'has-errors' : 'success'}`}>
            <div className="result-summary">
              <span className="result-icon">
                {importResult.errors.length === 0 ? (
                  <FontAwesomeIcon icon={faCheck} />
                ) : (
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                )}
              </span>
              <span className="result-text">
                <strong>{importResult.created}</strong> created,{' '}
                <strong>{importResult.skipped}</strong> skipped
                {importResult.errors.length > 0 && (
                  <>, <strong className="error-count">{importResult.errors.length}</strong> errors</>
                )}
              </span>
            </div>
            {importResult.errors.length > 0 && (
              <div className="result-errors">
                <h4>Errors:</h4>
                <ul>
                  {importResult.errors.map((err, index) => (
                    <li key={index}>
                      <strong>{err.permission} → {err.feature}.{err.operation}:</strong> {err.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Input Section */}
        <div className="input-section">
          <div className="input-header">
            <label htmlFor="perm-json-input">Paste JSON or upload an export file</label>
            <div className="input-actions">
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faUpload} /> Upload JSON File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>
          <textarea
            id="perm-json-input"
            className={`json-textarea ${validationErrors.length > 0 ? 'has-errors' : ''}`}
            placeholder={`Paste an export file or JSON array, e.g.:
{
  "version": 1,
  "mappings": [
    { "permission": "admin", "feature": "wildfires", "operation": "create" }
  ]
}`}
            value={jsonInput}
            onChange={handleJsonChange}
            rows={8}
          />
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="validation-errors">
            <h4>Validation Errors:</h4>
            <ul>
              {validationErrors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Table */}
        {parsedMappings.length > 0 && validationErrors.length === 0 && (
          <div className="preview-section">
            <h3>Preview ({parsedMappings.length} mappings)</h3>
            <div className="preview-table-container">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Permission</th>
                    <th>Feature</th>
                    <th>Operation</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedMappings.map((m, index) => (
                    <tr key={index}>
                      <td className="mapping-permission">{m.permission}</td>
                      <td>{m.feature}</td>
                      <td>{m.operation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="modal-actions">
          <button
            onClick={() => onClose(!!didImport)}
            className="btn btn-secondary"
            disabled={importing}
          >
            {didImport ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleImport}
            className="btn btn-primary"
            disabled={importing || !isValid || !!didImport}
          >
            {importing ? (
              <>
                <FontAwesomeIcon icon={faRotate} spin /> Importing...
              </>
            ) : (
              `Import ${parsedMappings.length} Mapping${parsedMappings.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>
    </AppModal>
  );
};

export default ImportPermissionsModal;
